"use client"

import { Link } from "~/components/legacy/common/link"
import { ArrowRight } from "~/components/ui/icons"
import { AlternativeRank } from "~/components/web/alternatives/alternative-rank"
import { InlineMenu } from "~/components/web/inline-menu"
import { Favicon } from "~/components/web/ui/favicon"
import type { WebAlternativesPage } from "~/server/web/alternatives/queries"

export function AlternativePageSidebar({
  page,
}: {
  page: Pick<WebAlternativesPage, "sourceSlug" | "sourceName" | "sourceFaviconUrl" | "alternatives">
}) {
  const items = page.alternatives.map(alternative => ({
    id: `alternative-${alternative.slug}`,
    name: alternative.name,
    favicon: alternative.faviconUrl,
  }))
  return (
    <aside
      className="order-first min-w-0 self-start lg:order-last lg:sticky lg:top-24"
      aria-label={`${page.sourceName} comparison navigation`}
    >
      <details className="rounded-lg border p-4 lg:hidden">
        <summary className="cursor-pointer font-medium">Jump to an alternative</summary>
        <nav className="mt-3 grid" aria-label="Jump to alternative">
          {items.map((item, index) => (
            <Link
              key={item.id}
              href={`#${item.id}`}
              className="flex min-h-11 items-center gap-3 text-sm"
            >
              <AlternativeRank position={index + 1} variant="list" />
              {item.name}
            </Link>
          ))}
        </nav>
      </details>
      <div className="hidden space-y-6 lg:block">
        <section className="space-y-4 rounded-lg border bg-card p-5">
          <div className="flex items-center gap-3">
            <Favicon src={page.sourceFaviconUrl} title={page.sourceName} className="size-10" />
            <div className="min-w-0">
              <p className="text-xs text-secondary-foreground">Comparing</p>
              <p className="break-words font-semibold">{page.sourceName}</p>
            </div>
          </div>
          <Link
            href={`/tools/${page.sourceSlug}`}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-primary dark:text-indigo-300"
          >
            View tool profile <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </section>
        <div className="rounded-lg border bg-card py-4">
          <h2 className="px-5 text-sm font-semibold">On this page</h2>
          <InlineMenu
            items={items}
            collapsible={false}
            showHeader={false}
            navigationLabel="Alternatives on this page"
            className="mt-3 max-h-[50vh] px-2!"
            renderItem={(item, active, index) => (
              <Link
                key={item.id}
                href={`#${item.id}`}
                aria-current={active ? "location" : undefined}
                className={`flex min-h-11 items-center gap-3 rounded-md px-3 text-sm ${active ? "bg-accent font-medium text-foreground" : "text-secondary-foreground hover:bg-accent hover:text-foreground"}`}
              >
                <AlternativeRank position={index + 1} variant="list" />
                <Favicon src={item.favicon} title={item.name} className="size-5" />
                <span className="min-w-0 break-words">{item.name}</span>
              </Link>
            )}
          />
          <nav
            className="mx-4 mt-3 grid border-t pt-2 text-sm text-secondary-foreground"
            aria-label="Comparison notes"
          >
            <Link href="#contrast" className="flex min-h-11 items-center">
              Where they differ
            </Link>
            <Link href={`#stay-with-${page.sourceSlug}`} className="flex min-h-11 items-center">
              When to keep {page.sourceName}
            </Link>
          </nav>
        </div>
        <Link
          href="/alternatives"
          className="inline-flex min-h-11 items-center text-sm font-medium text-primary dark:text-indigo-300"
        >
          Browse all comparisons <ArrowRight className="ml-2 size-4" aria-hidden="true" />
        </Link>
      </div>
    </aside>
  )
}
